import type { FormatNumberOptions, IntlFormatters } from 'react-intl';

import type { Currency } from '@xingternal/crate-common-graphql-types';
import type {
  Salary,
  SalaryFragment,
  SalaryEstimateFragment,
  SalaryRangeFragment,
} from '@xingternal/crate-jobs-graphql';

export type SalaryValid = Omit<SalaryFragment, 'amount' | 'currency'> & {
  amount: number;
  currency: Currency;
};

export type SalaryEstimateValid = Omit<
  SalaryEstimateFragment,
  'maximum' | 'minimum' | 'currency'
> & {
  maximum: number;
  minimum: number;
  currency: Currency;
};

export type SalaryRangeValid = Omit<
  SalaryRangeFragment,
  'maximum' | 'minimum' | 'currency'
> & {
  maximum: number;
  minimum: number;
  currency: Currency;
};

export const getSalaryFormatOptions = (
  currency: Currency
): FormatNumberOptions => ({
  style: 'currency',
  currency: currency,
  minimumFractionDigits: 0,
});

export const formatSalary = (
  formatNumber: IntlFormatters['formatNumber'],
  [min, max]: number[],
  currency: Currency
): string => {
  const options = getSalaryFormatOptions(currency);

  return `${formatNumber(min, options)} – ${formatNumber(max, options)}`;
};

export const isValidSalary = (salary?: Salary | null): salary is SalaryValid =>
  salary?.__typename === 'Salary' &&
  typeof salary.amount === 'number' &&
  !!salary.currency;

export const isValidSalaryEstimate = (
  salary?: Salary | null
): salary is SalaryEstimateValid =>
  salary?.__typename === 'SalaryEstimate' &&
  typeof salary.maximum === 'number' &&
  typeof salary.minimum === 'number' &&
  !!salary.currency;

export const isValidSalaryRange = (
  salary?: Salary | null
): salary is SalaryRangeValid =>
  salary?.__typename === 'SalaryRange' &&
  typeof salary.maximum === 'number' &&
  typeof salary.minimum === 'number' &&
  !!salary.currency;
